import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Scale, ShieldAlert } from "lucide-react";
import { Reveal } from "../components/site/Reveal";
import { SectionHead, usePageMeta } from "../components/site/PageHero";
import { AffiliationBlock, LatestNews, ObjectivesGrid, SectorsGrid } from "../components/site/Sections";
import { LEGAL_SERVICES, MISSION, RIGHTS, RIGHTS_DISCLAIMER, WELFARE } from "../data/content";

const Hero = () => (
  <section className="relative overflow-hidden bg-navy text-white" data-testid="home-hero">
    <img src="/images/hero.jpg" alt="Kerala backwaters" className="absolute inset-0 h-full w-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
    <div className="absolute inset-0 grid-texture" />
    <div className="container-x relative grid items-center gap-12 py-20 md:py-28 lg:grid-cols-12">
      <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300">
          Voice. Rights. Welfare.
        </span>
        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          United for the rights of <span className="text-emerald-300">IT employees</span>
        </h1>
        <p className="mt-6 max-w-xl text-base text-slate-300 md:text-lg">
          Building a stronger, safer and more secure future for every employee in the IT and digital services sector.
        </p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to="/membership" className="btn-green !px-7 !py-3.5" data-testid="hero-join-button">JOIN THE FEDERATION <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/employee-rights" className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="hero-rights-button">KNOW YOUR RIGHTS</Link>
        </div>
      </motion.div>
      <motion.div className="lg:col-span-5 flex justify-center lg:justify-end" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
        <div className="relative w-full max-w-sm rounded-2xl border border-white/15 bg-white/10 p-5 text-center backdrop-blur-xl" data-testid="hero-logo-card">
          <div className="rounded-xl bg-white p-6 shadow-2xl">
            <img src="/images/logo.jpg" alt="IT Employees Federation official logo" className="mx-auto w-full max-w-[280px] object-contain" data-testid="hero-logo-image" />
          </div>
          <div className="pt-4 text-xs text-slate-300">Affiliated to STU – Swatantra Thozhilali Union</div>
        </div>
      </motion.div>
    </div>
  </section>
);

const Pillars = () => (
  <div className="bg-royal text-white">
    <div className="container-x flex flex-wrap items-center justify-center gap-x-10 gap-y-2 py-5 font-display text-sm font-bold tracking-[0.25em] md:justify-between">
      {["UNITY", "RIGHTS", "PROTECTION", "WELFARE", "EMPOWERMENT"].map((w) => <span key={w}>{w}</span>)}
    </div>
  </div>
);

const About = () => (
  <section className="py-16 md:py-24" data-testid="home-about">
    <div className="container-x grid gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <SectionHead eyebrow="About the Federation" title="An organised platform for the collective interests of IT-sector employees" />
        <p className="mt-6 text-base leading-relaxed text-slate-600">
          IT Employees Federation brings together employees of the Information Technology and digital services sector to protect their rights, interests, professional dignity and welfare — through awareness, representation and support.
        </p>
        <Link to="/about" className="btn-outline mt-8" data-testid="home-about-link">More about us <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="rounded-2xl bg-white p-8 shadow-sm border border-slate-200">
          <h3 className="font-display text-lg font-bold text-navy">Our mission is to ensure</h3>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {MISSION.map((m) => (
              <li key={m} className="flex items-start gap-3 text-sm font-medium text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-leaf" />{m}</li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

const RightsPreview = () => (
  <section className="py-16 md:py-24" data-testid="home-rights">
    <div className="container-x">
      <SectionHead eyebrow="Employee Rights & Protection" title="Know the rights that protect you at work" />
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {RIGHTS.map(({ icon: Icon, title }, i) => (
          <Reveal key={title} delay={i * 0.04}>
            <Link to="/employee-rights" className="group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 transition-colors hover:border-royal/40" data-testid={`home-right-${i}`}>
              <Icon className="h-6 w-6 shrink-0 text-royal" />
              <span className="text-sm font-semibold text-navy group-hover:text-royal">{title}</span>
            </Link>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 flex items-start gap-2 text-xs text-slate-500"><ShieldAlert className="h-4 w-4 shrink-0" />{RIGHTS_DISCLAIMER}</p>
    </div>
  </section>
);

const LegalBand = () => (
  <section className="bg-navy py-16 text-white md:py-24" data-testid="home-legal">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead light eyebrow="Legal Support & Assistance" title="When workplace problems need more than advice" text="Our legal support desk helps employees understand their options and connects them with qualified professionals where needed." />
        <Link to="/legal-support#request" className="btn-green mt-8" data-testid="home-legal-request-button"><Scale className="h-4 w-4" /> REQUEST LEGAL ASSISTANCE</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
        {LEGAL_SERVICES.map(({ icon: Icon, title }, i) => (
          <Reveal key={title} delay={i * 0.05}>
            <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5">
              <Icon className="h-6 w-6 shrink-0 text-emerald-300" /><span className="text-sm font-semibold">{title}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WelfareSection = () => (
  <section className="py-16 md:py-24" data-testid="home-welfare">
    <div className="container-x">
      <SectionHead eyebrow="Welfare & Empowerment" title="Initiatives for employees and their families" />
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {WELFARE.map(({ icon: Icon, title, text }, i) => (
          <Reveal key={title} delay={i * 0.04}>
            <div className="card-lift h-full rounded-xl border border-slate-200 bg-white p-6">
              <Icon className="h-7 w-7 text-leaf" />
              <h3 className="mt-4 font-display font-bold text-navy">{title}</h3>
              <p className="mt-2 text-sm text-slate-600">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const JoinCta = () => (
  <section className="pb-20" data-testid="home-join-cta">
    <div className="container-x">
      <div className="relative overflow-hidden rounded-2xl bg-leaf p-10 text-white md:p-14">
        <img src="/images/coconut.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl font-extrabold sm:text-3xl lg:text-4xl">Your voice is stronger together.</h2>
            <p className="mt-3 text-emerald-50">Apply for membership online in a few minutes and track your application status anytime.</p>
          </div>
          <Link to="/membership" className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-7 py-3.5 text-sm font-bold text-leaf-dark transition-colors hover:bg-emerald-50" data-testid="cta-join-button">JOIN THE FEDERATION <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(null);
  return (
    <>
      <Hero />
      <Pillars />
      <About />
      <section className="bg-slate-100/70 py-16 md:py-24" data-testid="home-affiliation"><div className="container-x"><AffiliationBlock /></div></section>
      <section className="py-16 md:py-24" data-testid="home-objectives">
        <div className="container-x"><SectionHead eyebrow="Our Objectives" title="What the federation works for" /><ObjectivesGrid /></div>
      </section>
      <section className="bg-navy py-16 text-white md:py-24" data-testid="home-members">
        <div className="container-x">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHead light eyebrow="Who can become a member?" title="Open to employees across the IT and digital services sector" />
            <Link to="/membership#eligibility" className="btn-green self-start" data-testid="home-eligibility-link">Check eligibility</Link>
          </div>
          <SectorsGrid dark />
        </div>
      </section>
      <RightsPreview />
      <LegalBand />
      <WelfareSection />
      <LatestNews />
      <JoinCta />
    </>
  );
}
